<template>
  <div
    ref="$el"
    :class="['d-textarea-wrapper', block ? 'd-block' : 'd-inline-block']"
  >
    <div
      ref="dropdownTarget"
      :class="cmpClass"
      @click="e => handleClick(e)"
      @mousedown="e => handleMousedown(e)"
      @mouseenter="e => handleMouseenter(e)"
      @mouseleave="e => handleMouseleave(e)"
      @mouseup="e => handleMouseup(e)"
    >
      <div
        v-if="$slots.prefix || prefix !== undefined"
        :class="prefixClass"
      >
        <slot
          v-if="$slots.prefix"
          name="prefix"
        />
        <div
          v-else
          class="d-text d-text-nowrap"
        >
          {{ prefix }}
        </div>
      </div>
      <textarea
        ref="textarea"
        :class="['d-text', monospace && 'd-text-monospace']"
        :style="textareaStyle"
        :value="modelValue"
        :placeholder="placeholder"
        :rows="rows"
        :disabled="disabled"
        @input="e => handleInput(e)"
        @focus="e => handleFocus(e)"
        @blur="e => !keepFocus && handleBlur(e)"
      />
      <textarea
        v-if="autosize"
        ref="shadow"
        tabindex="0"
        :class="['d-text', 'd-textarea-shadow',monospace && 'd-text-monospace']"
        :value="modelValue"
      />
      <div
        v-if="clearable || maxLength !== undefined || $slots.suffix || suffix !== undefined"
        class="d-textarea-suffix-wrapper"
      >
        <div class="d-textarea-suffix-fill-left" />
        <Icon
          v-if="clearable"
          :class="[suffixClass, clearClass]"
          theme="filled"
          :icon="CloseOne"
          @mousedown="e => handleClear(e)"
        />
        <div
          v-if="maxLength !== undefined"
          :class="[suffixClass, maxLengthClass]"
          :style="fadableIndicatorsStyle"
        >
          {{ modelValue?.length || 0 }}/{{ maxLength }}
        </div>
        <div
          v-if="$slots.suffix || suffix !== undefined"
          :class="suffixClass"
        >
          <slot
            v-if="$slots.suffix"
            name="suffix"
          />
          <div
            v-else
            class="d-text d-text-nowrap"
          >
            {{ suffix }}
          </div>
        </div>
      </div>
    </div>
    <Dropdown
      v-if="autoComplete && dropdownTarget"
      :target="dropdownTarget"
      :class="dropdownClass"
      :style="[
        $el && {
          minWidth: `${$el.clientWidth}px`,
          maxWidth: `${$el.clientWidth}px`,
        },
        dropdownStyle
      ]"
      trigger="manual"
      :visible="visible"
      :options="options?.map( name => ({ name, onClick() { handleUpdate(name) } }))"
      :mark="modelValue"
      :max-width="maxDropdownWidth"
      :max-height="maxDropdownHeight"
      @click:outside="handleClickOutside"
      @mousedown="setKeepFocus"
      @mouseup="clearKeepFocus"
      @mouseup:outside="clearKeepFocus"
    >
      <template #optionPrefix><span class="--space-p-left-small" /></template>
      <template
        v-if="loading"
        #content
      >
        <slot
          v-if="loading"
          name="loading"
        >
          <div class="d-input-popover-content">
            <Spinner tip="正在加载数据" />
          </div>
        </slot>
      </template>
    </Dropdown>
  </div>
</template>
<script setup lang="ts">
  import {
    ref, reactive, computed, watch, watchEffect, onMounted, onBeforeUnmount, nextTick, withDefaults, inject,
  } from 'vue'
  import { Dropdown } from '../Dropdown'
  import { Spinner } from '../Spinner'
  import { Icon } from '../Icon'
  import { FormItemReference, formItemProvision, FormItemProvision } from '../Form'
  import { CloseOne } from '../../icons'
  import { useDebounce, useDebounceWithResponse } from '../../composables'
  import { ValidateStatus, ValidateTiming } from '../../enums'
  import { MouseState } from '../../states'

  const props = withDefaults(
    defineProps<{
      modelValue?: string
      placeholder?: string
      prefix?: string
      suffix?: string
      rows?: number
      monospace?: boolean
      clearable?: boolean
      required?: boolean
      requiredError?: string
      maxLength?: number
      maxLengthError?: string
      validate?:(args: { modelValue?: string }) => string | boolean | Promise<string | boolean>
      validateDelay?: number
      validateTiming?: ValidateTiming
      validating?: boolean
      autosize?: boolean
      autofocus?: boolean
      autoComplete?: boolean
      options?: string[]
      maxDropdownWidth?: number
      maxDropdownHeight?: number
      loading?: boolean
      dropdownClass?: any
      dropdownStyle?: any
      block?: boolean
      disabled?: boolean
      fade?: boolean | {
        showIndicators?: boolean
        blankHighlight?: boolean
      }
      formItem?: boolean
    }>(),
    {
      modelValue: undefined,
      placeholder: undefined,
      prefix: undefined,
      suffix: undefined,
      monospace: false,
      rows: 4,
      clearable: false,
      required: false,
      requiredError: undefined,
      maxLength: undefined,
      maxLengthError: undefined,
      validate: undefined,
      validateDelay: undefined,
      validateTiming: undefined,
      validating: false,
      autosize: false,
      autofocus: false,
      autoComplete: false,
      options: undefined,
      maxDropdownWidth: undefined,
      maxDropdownHeight: undefined,
      loading: false,
      dropdownClass: undefined,
      dropdownStyle: undefined,
      block: false,
      disabled: false,
      fade: false,
      formItem: true,
    },
  )

  const emits = defineEmits<{(e: 'update:modelValue', modelValue: string): void; (e: 'update:validating', validating: boolean): void; (e: 'change', modelValue: string): void; (e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'input', event: Event): void; (e: 'focus', event: FocusEvent): void; (e: 'blur', event: FocusEvent): void; (e: 'clear', event: MouseEvent): void; (e: 'enter', event: KeyboardEvent, v: string): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const formItem = inject<FormItemProvision | undefined>(formItemProvision, undefined)
  const textarea = ref()
  const shadow = ref()
  const minHeight = ref()
  const modelValue = ref(props.modelValue)

  watch(
    modelValue,
    v => {
      emits('update:modelValue', v)
      emits('change', v)

      if (props.autosize) {
        nextTick(
          () => {
            if (shadow.value && minHeight.value !== shadow.value.scrollHeight) {
              minHeight.value = shadow.value.scrollHeight
            }
          },
        )
      }
    },
    {
      immediate: props.autosize,
    },
  )

  watch(
    () => props.modelValue,
    v => {
      modelValue.value = v
    },
  )

  const dropdownTarget = ref<HTMLElement>()

  const focus = ref(false)
  const visible = ref(false)

  const keepFocus = ref(false)

  const status = ref<ValidateStatus>('default')
  const validateTiming = computed<ValidateTiming>(
    () => props.validateTiming
      || (props.formItem && formItem?.validateTiming.value)
      || 'blur',
  )
  const validating = ref(false)
  const validateError = ref<string>()

  const validate = useDebounceWithResponse(
    () => {
      if (!validating.value) {
        validating.value = true
      }

      // eslint-disable-next-line no-nested-ternary
      const internalValid = (!props.required || modelValue.value?.length > 0)
        ? (props.maxLength === undefined || (modelValue.value?.length || 0) <= props.maxLength)
          ? true
          : (props.maxLengthError ?? false)
        : (props.requiredError ?? false)

      if (internalValid === true && props.validate) {
        status.value = 'waiting'

        let tempStatus: ValidateStatus = 'default'
        let tempError: string

        return Promise
          .resolve(
            props.validate({ modelValue: modelValue.value }),
          )
          .then(
            res => {
              if (res !== true) {
                tempStatus = 'error'

                if (typeof res === 'string') {
                  tempError = res
                }
              }

              return res
            },
          )
          .catch(
            () => {
              tempStatus = 'error'

              return '校验异常'
            },
          )
          .finally(
            () => {
              status.value = tempStatus
              validateError.value = tempError
            },
          )
      }

      status.value = internalValid === true ? 'default' : 'error'
      validateError.value = typeof internalValid === 'string' ? internalValid : undefined

      return Promise.resolve(internalValid)
    },
    { delay: props.validateDelay },
  )

  watch(
    validating,
    v => emits('update:validating', v),
  )

  watch(
    () => props.validating,
    v => {
      validating.value = v

      if (v) {
        validate()
      } else {
        status.value = 'default'
      }
    },
    { immediate: true },
  )

  watch(
    [
      () => props.required,
      () => props.maxLength,
      () => props.validate,
      modelValue,
    ],
    () => {
      if (validating.value || validateTiming.value === 'immediate') {
        validate()
      }
    },
    { immediate: true },
  )

  const fade = computed(
    () => (
      // eslint-disable-next-line no-nested-ternary
      typeof props.fade === 'object'
        ? props.fade
        : props.fade === true
          ? {
            showIndicators: false,
            blankHighlight: false,
          }
          : undefined
    ),
  )

  const fadableClass = computed(
    () => (
      // eslint-disable-next-line no-nested-ternary
      fade.value
        // eslint-disable-next-line no-nested-ternary
        ? focus.value
          ? '--color-bg-white'
          : (fade.value.blankHighlight && !modelValue.value?.length)
            ? '--color-bg-fill'
            : '--color-bg-fill-light'
        : '--color-bg-fill'
    ),
  )

  const fadableIndicatorsStyle = computed(
    () => (
      fade.value && {
        transition: 'opacity var(--time-transition-short)',
        opacity: (
          status.value === 'error'
          || state.hover
          || focus.value
          || fade.value.showIndicators
          || (fade.value.blankHighlight && !modelValue.value?.length)
        ) ? 1 : 0,
      }
    ),
  )

  const cmpClass = computed(
    () => {
      const general = [
        'd-textarea',
        props.disabled ? '--color-text-disabled' : '--color-text-title',
        props.disabled && 'disabled',
        state.pressing && 'pressing',
        ...focus.value
          ? ['focus', '--color-bg-static']
          : [],
      ]

      switch (status.value) {
        case 'error':
          return focus.value
            ? [
              ...general,
              fadableClass.value,
            ]
            : [
              ...general,
              '--color-bg-danger-light',
            ]
        default:
          return [
            ...general,
            fadableClass.value,
          ]
      }
    },
  )

  // const fadeStyle = computed(
  //   () => ({
  //     boxShadow: 'var(--shadow-none)',
  //     transition: 'border-color var(--time-transition-short), background-color var(--time-transition-short), box-shadow var(--time-transition-short), visibility var(--time-transition-short)',
  //     ...(state.hover || focus.value)
  //       && {
  //         boxShadow: 'var(--shadow-fade)',
  //       },
  //     ...((state.hover || (fade.value.blankHighlight && !modelValue.value?.length)) && !focus.value) && {
  //       borderColor: 'var(--color-border-default)',
  //     },
  //     ...status.value === 'default'
  //       && (
  //         (state.hover || focus.value || (fade.value.blankHighlight && !modelValue.value?.length))
  //           ? {
  //             backgroundColor: 'var(--color-white)',
  //           }
  //           : {
  //             backgroundColor: 'var(--color-transparent)',
  //           }
  //       ),
  //   }),
  // )

  const prefixClass = computed(
    () => [
      'd-textarea-prefix',
      props.disabled ? '--color-text-disabled' : '--color-text-description',
    ],
  )

  const suffixClass = computed(
    () => [
      'd-textarea-suffix',
      props.disabled ? '--color-text-disabled' : '--color-text-description',
    ],
  )

  const clearClass = computed(
    () => [
      'd-textarea-suffix-clear',
      props.disabled && 'disabled',
      state.hover && 'hover',
    ],
  )

  const maxLengthClass = computed(
    () => [
      'd-text',
      'd-text-nowrap',
      'd-textarea-suffix-maxlength',
    ],
  )

  const textareaStyle = computed(
    () => props.autosize && {
      minHeight: `${minHeight.value}px`,
    },
  )

  const changeFocus = useDebounce(
    (v: boolean, e: (FocusEvent | { e?: FocusEvent; immediate: boolean })) => {
      if (focus.value !== v) {
        focus.value = v
        visible.value = v

        if (v) {
          emits('focus', 'immediate' in e ? e.e : e)
        } else {
          emits('blur', 'immediate' in e ? e.e : e)

          if (validating.value !== true && validateTiming.value === 'blur') {
            validate()
          }
        }
      }
    },
    {
      immediate: (v, e) => (v || ('immediate' in e && e.immediate === true)),
    },
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
    textarea.value.focus()
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  function handleInput(e: Event) {
    emits('input', e)

    const target = e.target as HTMLInputElement

    modelValue.value = target.value
  }

  function handleFocus(e: FocusEvent) {
    changeFocus(true, e)
  }

  function handleBlur(e: FocusEvent) {
    changeFocus(false, e)
  }

  function handleClear(e: MouseEvent) {
    if (!props.disabled) {
      emits('clear', e)
      modelValue.value = ''
    }
  }

  function handleUpdate(v: string) {
    modelValue.value = v
    changeFocus(false, { immediate: true })
  }

  function handleClickOutside(e: MouseEvent) {
    changeFocus(false, e)
  }

  function setKeepFocus() {
    keepFocus.value = true
  }

  function clearKeepFocus() {
    keepFocus.value = false
  }

  function handleEnter(e: KeyboardEvent) {
    if (focus.value && e.key === 'Enter') {
      emits('enter', e, modelValue.value)
    }
  }

  onMounted(
    () => window.addEventListener('keydown', handleEnter),
  )

  onBeforeUnmount(
    () => window.removeEventListener('keydown', handleEnter),
  )

  if (props.autofocus) {
    onMounted(
      () => changeFocus(true, { immediate: true }),
    )
  }

  const $el = ref<HTMLElement>()

  const exposed = reactive<FormItemReference>({
    $el: $el.value,
    modelValue: modelValue.value,
    fullValue: modelValue.value,
    required: props.required,
    blur: () => changeFocus(false, { immediate: true }),
    focus: () => changeFocus(true, { immediate: true }),
    validate,
    reset() {
      modelValue.value = undefined
      validating.value = false
      status.value = 'default'
    },
    status: status.value,
    validateError: validateError.value,
    update(v: string) {
      modelValue.value = v
    },
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
      exposed.modelValue = modelValue.value
      exposed.fullValue = modelValue.value
      exposed.required = props.required
      exposed.status = status.value
      exposed.validateError = validateError.value
    },
  )

  watch(
    () => props.formItem,
    v => {
      if (v) {
        if (modelValue.value === undefined) {
          modelValue.value = formItem?.modelValue.value
        }

        formItem?.register(exposed)
      } else {
        formItem?.unregister(exposed)
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(
    () => {
      if (props.formItem) {
        formItem?.unregister(exposed)
      }
    },
  )

  defineExpose(exposed)
</script>
